import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Monitor, User, Mail, Lock, Phone, Briefcase, Globe, CheckCircle2, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    full_name: '', email: '', password: '', confirm_password: '',
    phone: '', skills: '', tools: '', portfolio_url: ''
  });

  useEffect(() => {
    axios.get(`${API}/auth/invite/${code}`)
      .then(r => {
        setInvite(r.data);
        if (r.data.artist_email) setForm(f => ({ ...f, email: r.data.artist_email }));
        setLoading(false);
      })
      .catch(e => { setError(e.response?.data?.detail || 'Invalid or expired invitation link'); setLoading(false); });
  }, [code]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) { setError('Passwords do not match'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/register-with-invite`, {
        invite_code: code,
        email: form.email,
        password: form.password,
        full_name: form.full_name,
        phone: form.phone,
        skills: form.skills.split(',').map(s => s.trim()).filter(Boolean),
        tools: form.tools.split(',').map(s => s.trim()).filter(Boolean),
        portfolio_url: form.portfolio_url,
      });
      setSuccess(true);
      localStorage.setItem('vos_token', res.data.token);
      setTimeout(() => { window.location.href = '/dashboard'; }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
      <div className="animate-pulse-slow text-zinc-600 text-sm font-mono">Validating invitation...</div>
    </div>
  );

  if (error && !invite) return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
      <div className="text-center">
        <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <p className="text-sm text-red-400">{error}</p>
        <a href="/login" className="text-xs text-blue-400 hover:underline mt-3 block">Go to Login</a>
      </div>
    </div>
  );

  if (success) return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
      <div className="text-center animate-fade-in">
        <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
        <p className="text-lg text-white font-heading font-bold">Welcome to VawworldOS!</p>
        <p className="text-xs text-zinc-500 mt-1">Registered as {invite?.role_label}. Redirecting to dashboard...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090b] relative overflow-hidden" data-testid="register-page">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-lg mx-4 z-10 animate-fade-in">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-3 mb-2">
            <div className="w-8 h-8 bg-blue-600 rounded-sm flex items-center justify-center">
              <Monitor className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-2xl font-heading font-bold tracking-tight text-white">VawworldOS</h1>
          </div>
          <p className="text-zinc-500 text-xs uppercase tracking-wider">Artist Registration</p>
        </div>

        {/* Role Badge */}
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-sm px-4 py-3 mb-4 text-center">
          <p className="text-xs text-emerald-400">You've been invited to join as</p>
          <p className="text-lg font-heading font-bold text-white mt-0.5">{invite?.role_label}</p>
          <p className="text-[10px] text-zinc-500">{invite?.vendor_name ? `${invite.vendor_name} · ` : ''}{invite?.department} | {invite?.skill_level} level</p>
        </div>

        <div className="bg-zinc-900/80 border border-zinc-800 rounded-sm p-5 backdrop-blur-sm">
          <form onSubmit={handleRegister} className="space-y-3">
            <div>
              <label className="block text-[10px] font-medium text-zinc-400 mb-1 uppercase tracking-wider">Full Name *</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                <input data-testid="reg-name" type="text" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} required
                  className="w-full h-9 pl-9 pr-3 bg-zinc-950 border border-zinc-800 rounded-sm text-sm text-white focus:ring-1 focus:ring-blue-500 focus:outline-none" placeholder="Your full name" />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-zinc-400 mb-1 uppercase tracking-wider">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                <input data-testid="reg-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required readOnly={!!invite?.artist_email}
                  className="w-full h-9 pl-9 pr-3 bg-zinc-950 border border-zinc-800 rounded-sm text-sm text-white focus:ring-1 focus:ring-blue-500 focus:outline-none" placeholder="your@email.com" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-medium text-zinc-400 mb-1 uppercase tracking-wider">Password *</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                  <input data-testid="reg-password" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required
                    className="w-full h-9 pl-9 pr-3 bg-zinc-950 border border-zinc-800 rounded-sm text-sm text-white focus:ring-1 focus:ring-blue-500 focus:outline-none" placeholder="Min 6 chars" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-zinc-400 mb-1 uppercase tracking-wider">Confirm *</label>
                <input data-testid="reg-confirm-password" type="password" value={form.confirm_password} onChange={e => setForm({...form, confirm_password: e.target.value})} required
                  className="w-full h-9 px-3 bg-zinc-950 border border-zinc-800 rounded-sm text-sm text-white focus:ring-1 focus:ring-blue-500 focus:outline-none" placeholder="Repeat password" />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-zinc-400 mb-1 uppercase tracking-wider">Phone</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                <input data-testid="reg-phone" type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                  className="w-full h-9 pl-9 pr-3 bg-zinc-950 border border-zinc-800 rounded-sm text-sm text-white focus:ring-1 focus:ring-blue-500 focus:outline-none" placeholder="+91-9876543210" />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-zinc-400 mb-1 uppercase tracking-wider">Skills (comma separated)</label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                <input data-testid="reg-skills" type="text" value={form.skills} onChange={e => setForm({...form, skills: e.target.value})}
                  className="w-full h-9 pl-9 pr-3 bg-zinc-950 border border-zinc-800 rounded-sm text-sm text-white focus:ring-1 focus:ring-blue-500 focus:outline-none" placeholder="3D Modeling, Texturing, Blender" />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-zinc-400 mb-1 uppercase tracking-wider">Tools (comma separated)</label>
              <input data-testid="reg-tools" type="text" value={form.tools} onChange={e => setForm({...form, tools: e.target.value})}
                className="w-full h-9 px-3 bg-zinc-950 border border-zinc-800 rounded-sm text-sm text-white focus:ring-1 focus:ring-blue-500 focus:outline-none" placeholder="Blender, Maya, ZBrush, Substance Painter" />
            </div>

            <div>
              <label className="block text-[10px] font-medium text-zinc-400 mb-1 uppercase tracking-wider">Portfolio URL</label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                <input data-testid="reg-portfolio-url" type="url" value={form.portfolio_url} onChange={e => setForm({...form, portfolio_url: e.target.value})}
                  className="w-full h-9 pl-9 pr-3 bg-zinc-950 border border-zinc-800 rounded-sm text-sm text-white focus:ring-1 focus:ring-blue-500 focus:outline-none" placeholder="https://artstation.com/yourname" />
              </div>
            </div>

            {error && <p className="text-red-400 text-xs">{error}</p>}

            <button data-testid="reg-submit" type="submit" disabled={submitting}
              className="w-full h-10 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-sm transition-all disabled:opacity-50 mt-2">
              {submitting ? 'Creating Account...' : 'Join VawworldOS'}
            </button>
          </form>
        </div>

        <p className="text-center text-[10px] text-zinc-700 mt-3">
          Already have an account? <a href="/login" className="text-blue-400 hover:underline">Sign In</a>
        </p>
      </div>
    </div>
  );
}
